import type { FunctionComponent, ReactNode } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { InlineAdSlotRenderProps } from "~/components/schema-render/common/inline-ad-slot/hooks";
import { useInlineAdSlotRender } from "~/components/schema-render/common/inline-ad-slot/hooks";
import { useTrackViewedPercentageRender } from "~/components/schema-render/common/track-viewed-percentage/hooks";
import { ContentSchemaRender } from "~/components/schema-render/content";
import type { Props as ContentSchemaRenderProps } from "~/components/schema-render/content";
import type { contentBodyContent$key } from "~/queries/__generated__/contentBodyContent.graphql";

import { ContentBodyContainer } from "./styles";

export type Props = {
  children?: ReactNode;
  inlineAdSlots?: InlineAdSlotRenderProps;
  preprocessSchema?: (schema: Schema) => Schema;
  reference: contentBodyContent$key;
} & Pick<ContentSchemaRenderProps, "advertising" | "extraComponentMap" | "extraRenderFunctionMap">;

export const ContentBody: FunctionComponent<Props> = ({
  advertising,
  children,
  extraComponentMap = {},
  extraRenderFunctionMap = {},
  inlineAdSlots = {},
  preprocessSchema = schema => schema,
  reference: reference_,
  ...attribs
}) => {
  const content = useFragment(
    graphql`
      fragment contentBodyContent on Content
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        entityId
        body(customContents: $customContents) {
          json
        }
        ...contentSchemaRenderContent
      }
    `,
    reference_,
  );

  const { inlineAdSlotRenders } = useInlineAdSlotRender(content.entityId, inlineAdSlots);
  const { trackViewedPercentageRenders } = useTrackViewedPercentageRender();

  const schema = useMemo(() => {
    const bodyJson = content?.body?.json;
    return bodyJson ? preprocessSchema(bodyJson) : null;
  }, [content?.body?.json, preprocessSchema]);

  if (!content.body?.json || schema?.length === 0) return null;
  return (
    <ContentBodyContainer {...attribs}>
      <ContentSchemaRender
        advertising={advertising}
        extraComponentMap={{
          ...inlineAdSlotRenders,
          ...trackViewedPercentageRenders,
          ...extraComponentMap,
        }}
        extraRenderFunctionMap={extraRenderFunctionMap}
        reference={content}
        schema={schema}
      />
      {children}
    </ContentBodyContainer>
  );
};

ContentBody.displayName = "ContentBody";
